import axios from 'axios';
import globalStore from '$$globalStore';
import _ from 'lodash';
import app from '$$app';
import ganttFilterHelper from '../../../helpers/gantt_filter';
import listViewHelpers from '../helpers';
import rights from '../../../components/rights';
import calendarModule from '../../../components/calendar';
import userCustomColumnModule from '../../../components/userCustomColumns';
import pricingHelper from '../../../helpers/pricingHelper';
import listViewConstants from '../constants';
import constants from '$$helpers/constants';

const ListViewController = {
  getPreparedHeadersByParams(projectsColumns, activeGanttIds, isProjectType) {
    const availableDefaultColumns = pricingHelper.getAccessToDefaultColumns([
      ...listViewConstants.COLUMNS.PREPEND_COLUMNS,
      ...listViewConstants.COLUMNS.DEFAULT_COLUMNS,
    ]);

    const columns = listViewConstants.COLUMNS.DEFAULT_COLUMNS.filter(col => {
      if (isProjectType && col.property === 'project_name') return false;

      if (availableDefaultColumns.includes(col.property)) return col;

      return false;
    });

    if (pricingHelper.checkPricingAccess('custom_fields')) {
      projectsColumns.forEach(projectColumns => {
        projectColumns?.columns.forEach(columnId => {
          const isAddedColumn = columns.find(col => col.id === columnId);

          if (isAddedColumn) {
            return;
          }

          const column = listViewHelpers.getPreparedCustomColumnById(columnId);

          if (column) {
            column.ganttIds = column.ganttIds.filter(id => activeGanttIds.includes(id));
            column.tooltipExtra = listViewHelpers.createTooltipInfoForCustomColumn(columnId, activeGanttIds);
            column.resizable = true;

            columns.push(column);
          }
        });
      });
    }

    const addCostCol = activeGanttIds.some(gantt_id => rights.project.hasCostRight(gantt_id));

    if (addCostCol && pricingHelper.checkPricingAccess('costs')) {
      columns.splice(6, 0, ...listViewConstants.COLUMNS.COST_COLUMNS);
    }

    columns.map(item => {
      if (item.id === 'status') {
        activeGanttIds.map(ganttId => item.forJiraProject[ganttId] = globalStore.getters['columns/getJiraColumnByNameAndProjectId']('status', ganttId));
      }

      if (item.id === 'priority') {
        activeGanttIds.map(ganttId => item.forJiraProject[ganttId] = globalStore.getters['columns/getJiraColumnByNameAndProjectId']('priority', ganttId));
      }
    });

    return columns;
  },

  getPreparedTasksByParams(tasks, projectsColumns, filterState) {
    const customColumnsIsLoaded = globalStore.getters['columns/valuesLoaded'];

    if (!customColumnsIsLoaded) return;

    const hasFilterValues = filterState && Object.values(filterState).some(value => value || ((typeof value === 'string' || Array.isArray(value)) && value.length > 0));
    const customColumns = pricingHelper.checkPricingAccess('custom_fields')
      ? projectsColumns.reduce((columns, project) => Object.assign(columns, listViewHelpers.getCustomColumnsByProject(project)), {})
      : {};

    const preparedTasks = listViewHelpers.prepareTasks(tasks, customColumns);

    let filtredTasks = [];

    if (filterState && hasFilterValues) {
      const filter = ganttFilterHelper.getFilter({
        options: filterState,
        value: filterState.text || '',
      }, null);

      filtredTasks = preparedTasks.filter(task => filter(task));

      if (!filtredTasks.length) {
        globalStore.commit('filter/isNotFound', true);
        // app.trigger('filter:tasks:not:found');
      } else {
        globalStore.commit('filter/isNotFound', false);
      }
    } else {
      filtredTasks = preparedTasks;
    }

    globalStore.commit('headerView/setListTasksCount', filtredTasks.length);
    globalStore.commit('headerView/setMyTasksFilter', listViewHelpers.getMyTasksFilterStatus(filterState));

    return filtredTasks;
  },

  async getConfig(project_id, multiview_id) {
    const res = await axios.get('api/list', { user_id: user.id });

    let config = _.find(res.data, { project_id, multiview_id });

    if (!config) {
      const defaultConf = {
        multiview_id,
        project_id,
        columns: JSON.stringify(listViewConstants.CONFIG.DEFAULT_COLUMNS),
        settings: JSON.stringify(listViewConstants.CONFIG.DEFAULT_SETTINGS),
      };

      const res = await axios.post('api/list', defaultConf);

      config = _.find(res.data, { project_id, multiview_id });
    }

    const result = listViewHelpers.getSettingsFromConfig(config);

    globalStore.commit('headerView/assignViewSettings', result.viewSettings);
    globalStore.commit('headerView/setOverdueTasks', result.overdue);

    return result;
  },

  async updateConfig(currentConfig, update) {
    if (!update.columns && !update.settings) {
      return currentConfig;
    }

    const body = {
      project_id: currentConfig.project_id,
      multiview_id: currentConfig.multiview_id,
    };

    if (update.columns) {
      body.columns = JSON.stringify(update.columns);
    }

    if (update.settings) {
      body.settings = JSON.stringify(update.settings);
    }

    const response = await axios.put(`api/list/${currentConfig.id}`, body);
    const newConfig = _.find(response.data, { project_id: currentConfig.project_id, multiview_id: currentConfig.multiview_id });

    if (!newConfig) {
      return currentConfig;
    }

    return listViewHelpers.getSettingsFromConfig(newConfig);
  },

  getCalendarsConfigs(ganttIds) {
    return ganttIds.reduce((configs, ganttId) => {
      configs[ganttId] = calendarModule.getCalendarConfig(+ganttId);

      return configs;
    }, {});
  },

  async createItem(item, projectId) {
    const parentId = globalStore.getters['tasksModel/getTotalEstimateDataForProject'](projectId).id;
    const tasksToCreate = [{
      text: item.name, type: item.type, parent: parentId, unit: item.unit,
    }];

    await globalStore.dispatch('tasksModel/createTasks', {
      tasks: tasksToCreate,
      sourceEvent: {
        type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_CREATE,
        sourceId: null,
      },
      fromGantt: false,
    });
  },

  async changeTask(edit) {
    const originalTaskData = globalStore.getters['tasksModel/getTaskByGanttId'](edit.task.gantt_id, edit.task.id);
    const value = listViewHelpers.validateTaskValue(edit.value, edit.property, originalTaskData);
    const tasksToUpdate = [{ id: edit.task.id, [edit.property]: value }];

    await globalStore.dispatch('tasksModel/updateTasks', {
      tasks: tasksToUpdate,
      sourceEvent: {
        type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_UPDATE,
        sourceId: edit.task.id,
      },
      fromGantt: false,
    });
  },

  changeTaskCustom(edit) {
    const originalTaskData = globalStore.getters['tasksModel/getTaskByGanttId'](edit.task.gantt_id, edit.task.id);
    // edit.task[edit.column.property] = edit.value;

    let value = typeof edit.value === 'undefined' || edit.value == null ? '' : edit.value;

    if (edit.column.isOptions) {
      if (Array.isArray(value)) {
        value = value.map(i => i.id).join(',');
      }
      if (_.isObject(value)) {
        value = value.id.toString();
      }
    }

    userCustomColumnModule.changeUserCustomValue(originalTaskData, edit.column.id, value);
  },

  async deleteTask(task) {
    const tasksToDelete = [task];

    await globalStore.dispatch('tasksModel/deleteTasks', {
      tasks: tasksToDelete,
      sourceEvent: {
        type: constants.GANTT_SOURCE_EVENT_TYPES.TASK_DELETE,
        sourceId: task.id,
      },
      fromGantt: false,
    });

    return true;
  },
};

export default ListViewController;
